import React from "react";
import {
  MdOutlineEmail,
  MdOutlineLocationOn,
  MdOutlinePhone,
  MdOutlineSchedule,
  MdOutlineSend,
} from "react-icons/md";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { contactSupport } from "@/utils/content";
import classes from "@/styles/contact/contact-support-main.module.css";
import InputGroup from "../common/input-group";
import { sendSupportEmail } from "@/services/connectWithUsServices";
import useAlertBar from "@/hooks/useAlertBar";
import { useIsMobile } from "@/hooks/useIsMobile";

type Inputs = {
  subject: string;
  email: string;
  phone: string;
  name: string;
  company: string;
  message: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  subject: yup.string().required("Subject is required"),
  name: yup.string().required("Full Name is required"),
  message: yup.string().required("Message is required"),
});

const ContactSupportMain = () => {
  const content = contactSupport.en;
  const isMobile = useIsMobile();

  const { openAlertBar } = useAlertBar();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema) as any,
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    let res = await sendSupportEmail(data);

    if (res.data.status) {
      openAlertBar("Your Support Request was submitted successfully", true);
    } else {
      openAlertBar("Error Submitting Your Support Query", false);
    }
  };

  return (
    <main>
      <Container>
        <section className="my-5">
          <div className="text-center text-grey">
            <h1 className="h2">{content.title1}</h1>
            <div className="d-flex justify-content-center align-items-center">
              <MdOutlineSend />
              <p className="m-0 ms-2">{content.para1}</p>
            </div>
            <p className="mt-2 mb-0">{content.title2}</p>
            <small className="">{content.para2}</small>
          </div>
        </section>
        <section className={`${isMobile ? "" : "w-75"} my-5 mx-auto`}>
          <Row>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Col xs={12}>
                <InputGroup
                  required
                  label={content.subject}
                  name="subject"
                  register={register}
                  error={errors.subject?.message}
                />
              </Col>
              <Row>
                <Col xs={12} lg={6}>
                  <InputGroup
                    required
                    label={content.fullname}
                    name="name"
                    register={register}
                    error={errors.name?.message}
                  />
                </Col>
                <Col xs={12} lg={6}>
                  <InputGroup
                    required
                    label={content.email}
                    name="email"
                    register={register}
                    error={errors.email?.message}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={6}>
                  <InputGroup
                    label={content.phone}
                    name="phone"
                    register={register}
                    error={errors.phone?.message}
                  />
                </Col>
                <Col xs={12} lg={6}>
                  <InputGroup
                    label={content.company}
                    name="company"
                    register={register}
                    error={errors.company?.message}
                  />
                </Col>
              </Row>
              <Col xs={12}>
                <InputGroup
                  required
                  label={content.message}
                  name="message"
                  register={register}
                  error={errors.message?.message}
                />
              </Col>
              <div className="text-center my-3">
                <Button className="btn-lightblue" type="submit">
                  Submit
                </Button>
              </div>
            </form>
          </Row>
        </section>
        <section className="my-5">
          <Row className="mb-3">
            <Col className="mb-3" xs={12} md={4}>
              <Card className={classes.card}>
                <CardBody className="text-center">
                  <h5>{content.title3}</h5>
                  <div className="d-flex align-items-center justify-content-center">
                    <MdOutlineLocationOn className="me-2" />
                    <div>
                      <small>
                        9 Royalcrest Rd.
                        <br />
                        Etobicoke, ON M9V 2L6
                      </small>
                      <small>
                        Phone: 905-346-8044, <br />
                        855-434-3477 (Toll Free)
                      </small>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col className="mb-3" xs={12} md={4}>
              <Card className={classes.card}>
                <CardBody className="text-center">
                  <h5 className="mt-3">{content.title4}</h5>
                  <MdOutlineEmail className="me-2" />
                  <small>sales@shipvista.com</small>
                  <br />
                  <MdOutlinePhone className="me-2" />
                  <small>855-434-3477 ext. 1</small>
                </CardBody>{" "}
              </Card>
            </Col>

            <Col className="mb-3" xs={12} md={4}>
              <Card className={classes.card}>
                <CardBody className="text-center">
                  <h5 className="mt-3">{content.title5}</h5>

                  <MdOutlineEmail className="me-2" />
                  <small>customercare@shipvista.com</small>
                  <br />

                  <MdOutlinePhone className="me-2" />
                  <small>855-434-3477 ext. 2</small>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col className="mb-3" xs={12} md={4}>
              <Card className={classes.card}>
                <CardBody className="text-center">
                  <h5 className="mt-3">{content.title6}</h5>
                  <div className="d-flex align-items-center justify-content-center">
                    <MdOutlineSchedule className="me-2" />
                    <div>
                      <small className="d-block">Monday to Friday:</small>
                      <small>10 am to 7:30 pm EST</small>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col className="mb-3" xs={12} md={4}>
              <Card className={classes.card}>
                <CardBody className="text-center">
                  <h5 className="mt-3">{content.title7}</h5>

                  <MdOutlineEmail className="me-2" />
                  <small>finance@shipvista.com</small>
                  <br />

                  <MdOutlinePhone className="me-2" />
                  <small>855-434-3477 ext. 3</small>
                </CardBody>
              </Card>
            </Col>
            <Col className="mb-3" xs={12} md={4}>
              <Card className={classes.card}>
                <CardBody className="text-center">
                  <h5 className="mt-3">{content.title8}</h5>
                  <MdOutlineEmail className="me-2" />
                  <small>techsupport@shipvista.com</small>
                  <br />
                  <MdOutlinePhone className="me-2" />
                  <small>855-434-3477</small>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </section>
      </Container>
    </main>
  );
};

export default ContactSupportMain;
